import React, { useCallback, useMemo, useRef, useState } from 'react';
import { UploadedDocumentSummary } from '../../../types';
import { apiService } from '../../../services';
import styles from './DocumentUpload.module.css';

const ACCEPTED_EXTENSIONS = ['.txt', '.md', '.markdown', '.pdf'];
const ACCEPTED_MIME_TYPES = ['text/plain', 'text/markdown', 'application/pdf'];
const BYTES_IN_MB = 1024 * 1024;

interface UploadItem {
  id: string;
  name: string;
  size: number;
  progress: number;
  status: 'uploading' | 'error';
  error?: string;
  file: File;
}

interface DocumentUploadProps {
  documents: UploadedDocumentSummary[];
  contextId: string;
  onDocumentUploaded: (document: UploadedDocumentSummary) => void;
  onDocumentRemoved: (documentId: string) => Promise<void>;
  onError?: (message: string) => void;
  maxFileSizeMB?: number;
}

const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getFallbackMime = (filename: string) => {
  if (filename.toLowerCase().endsWith('.pdf')) return 'application/pdf';
  return 'text/plain';
};

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const validateFile = (file: File, maxFileSizeMB: number): string | null => {
  if (file.size > maxFileSizeMB * BYTES_IN_MB) {
    return `"${file.name}" exceeds the ${maxFileSizeMB}MB limit.`;
  }

  const mime = (file.type || '').toLowerCase();
  const name = file.name.toLowerCase();
  const isValidMime = ACCEPTED_MIME_TYPES.includes(mime);
  const isValidExt = ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));

  if (!isValidMime && !isValidExt) {
    return `Only TXT, MD, or PDF files are allowed. "${file.name}" is not supported.`;
  }

  return null;
};

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  documents,
  contextId,
  onDocumentUploaded,
  onDocumentRemoved,
  onError,
  maxFileSizeMB = 10,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeUploads, setActiveUploads] = useState<UploadItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const acceptedTypes = useMemo(
    () => [...ACCEPTED_EXTENSIONS, ...ACCEPTED_MIME_TYPES].join(','),
    []
  );

  const uploadFile = useCallback(
    async (file: File, retryId?: string) => {
      if (!contextId) {
        const message = 'No hay un identificador de sesión para subir documentos.';
        setLocalError(message);
        onError?.(message);
        return;
      }

      const uploadId = retryId || generateId();
      if (localError) {
        setLocalError(null);
      }

      setActiveUploads((prev) => {
        const filtered = prev.filter((item) => item.id !== uploadId);
        return [
          ...filtered,
          {
            id: uploadId,
            name: file.name,
            size: file.size,
            progress: 0,
            status: 'uploading',
            file,
          },
        ];
      });

      try {
        const document = await apiService.uploadDocument(
          contextId,
          file,
          (progress) => {
            setActiveUploads((prev) =>
              prev.map((item) =>
                item.id === uploadId ? { ...item, progress } : item
              )
            );
          }
        );

        setActiveUploads((prev) =>
          prev.filter((item) => item.id !== uploadId)
        );

        onDocumentUploaded({
          id: document.id || uploadId,
          name: document.filename || file.name,
          size: file.size,
          type: file.type || getFallbackMime(file.name),
          status: 'uploaded',
          progress: 100,
          uploadedAt: document.uploadedAt || new Date().toISOString(),
          filePath: document.filePath,
          source: 'remote',
        });
      } catch (error: any) {
        let message = 'Failed to upload file.';
        
        // Handle duplicate document error (409 Conflict)
        if (error.status === 409) {
          // The error message from backend already contains the duplicate file info
          message = error.message || 'This document already exists in the system. Documents with identical content cannot be uploaded.';
        } else if (error instanceof Error) {
          message = error.message;
        } else if (error.message) {
          message = error.message;
        }
        
        setActiveUploads((prev) =>
          prev.map((item) =>
            item.id === uploadId
              ? { ...item, status: 'error', error: message, progress: 100 }
              : item
          )
        );
        setLocalError(message);
        onError?.(message);
      }
    },
    [contextId, onDocumentUploaded, onError, localError]
  );

  const handleFiles = useCallback(
    (fileList: FileList | File[]) => {
      const files = Array.from(fileList);

      files.forEach((file) => {
        const validationError = validateFile(file, maxFileSizeMB);
        if (validationError) {
          setLocalError(validationError);
          onError?.(validationError);
          return;
        }

        uploadFile(file);
      });
    },
    [maxFileSizeMB, onError, uploadFile]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      handleFiles(event.target.files);
      event.target.value = '';
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFiles(event.dataTransfer.files);
      event.dataTransfer.clearData();
    }
  };

  const handleRemoveDocument = async (documentId: string) => {
    try {
      await onDocumentRemoved(documentId);
    } catch (error) {
      console.error('Error removing document:', error);
      // The error is already handled in the parent component
    }
  };

  const retryUpload = (item: UploadItem) => {
    setActiveUploads((prev) => prev.filter((upload) => upload.id !== item.id));
    uploadFile(item.file);
  };

  const removeUploadItem = (itemId: string) => {
    setActiveUploads((prev) => {
      const filtered = prev.filter((upload) => upload.id !== itemId);
      // Clear local error if this was the last item with error
      const remainingErrors = filtered.filter(
        (upload) => upload.status === 'error'
      );
      if (remainingErrors.length === 0) {
        setLocalError(null);
      }
      return filtered;
    });
  };

  return (
    <div className={styles.container}>
      <div
        className={`${styles.dropZone} ${
          dragActive ? styles.dropZoneActive : ''
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <div className={styles.dropZoneIcon}>
          <i className="fas fa-file-upload" aria-hidden="true"></i>
        </div>
        <div className={styles.dropZoneText}>
          Drag and drop your files here or{' '}
          <span className={styles.dropZoneLink}>click to browse</span>
        </div>
        <p className={styles.dropZoneHint}>
          We accept TXT, MD, or PDF (max. {maxFileSizeMB}MB per file)
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={acceptedTypes}
        multiple
        className={styles.hiddenInput}
        onChange={handleInputChange}
      />

      {(localError) && (
        <div className={styles.errorMessage}>
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
          <span>{localError}</span>
        </div>
      )}

      {activeUploads.length > 0 && (
        <div className={styles.uploadList}>
          {activeUploads.map((item) => (
            <div key={item.id} className={styles.uploadItem}>
              <div className={styles.uploadMeta}>
                <div>
                  <p className={styles.uploadName}>{item.name}</p>
                  <span className={styles.uploadSize}>
                    {formatBytes(item.size)}
                  </span>
                </div>
                <span className={styles.uploadStatus}>
                  {item.status === 'uploading' ? `${item.progress}%` : 'Error'}
                </span>
              </div>
              {/* <div className={styles.progressBar}>
                <div
                  className={styles.progress}
                  style={{ width: `${Math.min(item.progress, 100)}%` }}
                />
              </div> */}
              {item.status === 'error' && (
                <div className={styles.uploadError}>
                  <div className={styles.errorActions}>
                    <button
                      type="button"
                      className={styles.retryButton}
                      onClick={() => retryUpload(item)}
                    >
                      Retry
                    </button>
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => removeUploadItem(item.id)}
                      title="Remove from list"
                      aria-label="Remove from list"
                    >
                      <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className={styles.documentList}>
        {documents.length === 0 ? (
          <div className={styles.emptyState}>
            <i className="fas fa-folder-open" aria-hidden="true"></i>
            <p>No documents added yet.</p>
          </div>
        ) : (
          documents.map((document) => (
            <div key={document.id} className={styles.documentItem}>
              <div className={styles.documentInfo}>
                <i className="fas fa-file-alt" aria-hidden="true"></i>
                <div>
                  <p className={styles.documentName}>{document.name}</p>
                  <span className={styles.documentMeta}>
                    {formatBytes(document.size)} ·{' '}
                    {new Date(document.uploadedAt).toLocaleString()}
                  </span>
                </div>
              </div>
              <div className={styles.documentActions}>
                <span className={styles.documentStatus}>
                  {document.status === 'uploaded' ? 'Ready' : document.status}
                </span>
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => handleRemoveDocument(document.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DocumentUpload;

